import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

interface AuthModalContextType {
  isOpen: boolean;
  openLoginModal: (onSuccess?: () => void) => void;
  closeLoginModal: () => void;
  runOnSuccess: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export const AuthModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onSuccessRef = useRef<(() => void) | null>(null);

  const openLoginModal = useCallback((onSuccess?: () => void) => {
    onSuccessRef.current = onSuccess ?? null;
    setIsOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsOpen(false);
    onSuccessRef.current = null;
  }, []);

  const runOnSuccess = useCallback(() => {
    const cb = onSuccessRef.current;
    onSuccessRef.current = null;
    if (cb) setTimeout(cb, 0);
  }, []);

  return (
    <AuthModalContext.Provider value={{ isOpen, openLoginModal, closeLoginModal, runOnSuccess }}>
      {children}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = (): AuthModalContextType => {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
};
